//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

/**
 * Whether a plugin-supplied URL points at this application's own origin.
 *
 * Asked of the URL parser rather than of the string, because the string
 * cannot be read the way the browser reads it.  For a special scheme the
 * WHATWG parser treats a backslash as a slash, so `/\evil.example/x` starts
 * with one slash, does not start with two, and still resolves to
 * `https://evil.example/x` -- as does `/\/evil.example/x`.  A prefix test
 * that rejects `//` catches the protocol-relative form and neither of those,
 * and every further quirk would have to be enumerated here as it is found.
 *
 * Parsing instead asks the question the sinks actually ask.  An iframe `src`
 * and an axios request both resolve against the document, so a URL this
 * agrees is same-origin is one they will fetch from this host, and one it
 * rejects is one no spelling can smuggle past.
 *
 * A URL the parser cannot read at all is not same-origin: there is nothing to
 * compare, and a manifest that produced one is malformed either way.  An
 * opaque origin -- what `javascript:` and `data:` parse to -- fails the
 * comparison on its own, since `origin` is the string `"null"` there and
 * never equal to a real one.
 */
export const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url, window.location.origin).origin ===
      window.location.origin;
  } catch {
    return false;
  }
};
